import { Mutation } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { ID } from 'shared/types/id';

const FIRESTORE_ERROR_CODES = new Set([
  'cancelled',
  'unknown',
  'invalid-argument',
  'deadline-exceeded',
  'not-found',
  'already-exists',
  'permission-denied',
  'resource-exhausted',
  'failed-precondition',
  'aborted',
  'out-of-range',
  'unimplemented',
  'internal',
  'unavailable',
  'data-loss',
  'unauthenticated',
]);

const isFirestoreError = (error: unknown): error is FirebaseError =>
  error instanceof FirebaseError && FIRESTORE_ERROR_CODES.has(error.code);

type VariablesShapeNode =
  | string
  | { [key: string]: VariablesShapeNode | VariablesShapeNode[] };

export type MutationErrorContext = {
  mutationKey: unknown;
  variablesShape: VariablesShapeNode;
  uid: ID | undefined;
  // The Firestore status code when the failure is one, so Firestore-shaped failures stay as
  // greppable as they were. `null` is the interesting value: an error that is not a Firestore error
  // at all — exactly what this function used to drop on the floor.
  firestoreCode: string | null;
  // Constructor/name of the thrown value, so a noisy class can be grouped and ignored in Sentry
  // without another code change here.
  errorKind: string;
};

const MAX_DEPTH = 5;

const getVariablesShape = (value: unknown, depth = 0): VariablesShapeNode => {
  if (value === null) {
    return 'null';
  }
  if (typeof value !== 'object') {
    return typeof value;
  }
  if (depth >= MAX_DEPTH) {
    return 'object[max-depth]';
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return 'array[]';
    }

    const elementShapes = value.map((element) =>
      getVariablesShape(element, depth + 1),
    );
    const uniqueShapes = Array.from(
      new Map(
        elementShapes.map((shape) => [JSON.stringify(shape), shape]),
      ).values(),
    );
    return { __array__: uniqueShapes };
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, child]) => [
      key,
      getVariablesShape(child, depth + 1),
    ]),
  );
};

const getErrorKind = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name || error.constructor?.name || 'Error';
  }
  return typeof error;
};

/*
  Every mutation failure is reported, not only the Firestore-shaped ones.

  This used to return null for anything that was not a FirebaseError carrying a Firestore status
  code, and the caller took that as "do not report". That inverted the value of the signal: a
  permission-denied is a failure we already understand, while an unexpected throw inside a mutation
  — a plain Error, a TypeError, a rejected callable — is the one nobody can reconstruct afterwards.
  Those vanished without a trace, which is how a goal that could not be deleted produced no Sentry
  event at all while the user sat looking at the failure dialog.
*/
export const getMutationErrorContext = (
  error: unknown,
  variables: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
  uid: ID | undefined,
): MutationErrorContext => ({
  mutationKey: mutation.options.mutationKey,
  variablesShape: getVariablesShape(variables),
  uid,
  firestoreCode: isFirestoreError(error) ? error.code : null,
  errorKind: getErrorKind(error),
});
